import type { PipelineReviewStageInfo } from "../lib/types";

const STAGE_ORDER = ["business_case", "product_spec", "architecture", "epic_plan"] as const;

const STAGE_LABELS: Record<string, string> = {
  business_case: "Business Case",
  product_spec: "Product Spec",
  architecture: "Architecture",
  epic_plan: "Epic Plan",
};

interface PipelineProgressProps {
  stages: Record<string, PipelineReviewStageInfo>;
  currentStage: string;
  onStageClick?: (stage: string) => void;
}

export default function PipelineProgress({
  stages,
  currentStage,
  onStageClick,
}: PipelineProgressProps) {
  const approvedCount = STAGE_ORDER.filter(
    (s) => stages[s]?.status === "approved",
  ).length;
  const percent = Math.round((approvedCount / STAGE_ORDER.length) * 100);

  return (
    <div className="flex flex-col gap-2">
      {/* Stage buttons */}
      <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto pb-1">
        {STAGE_ORDER.map((stage, i) => {
          const info = stages[stage];
          const status = info?.status || "pending";
          const isCurrent = stage === currentStage;
          const isClickable = status === "approved" || status === "reviewing";

          return (
            <div key={stage} className="flex items-center gap-1 sm:gap-2">
              {i > 0 && (
                <div
                  className={`w-4 sm:w-6 h-px flex-shrink-0 ${
                    status === "approved" ? "bg-green-500/60" : "bg-slate-700"
                  }`}
                />
              )}
              <button
                onClick={() => isClickable && onStageClick?.(stage)}
                disabled={!isClickable}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  status === "approved"
                    ? "bg-green-900/30 text-green-400 border border-green-500/30 hover:bg-green-900/50 cursor-pointer"
                    : status === "failed"
                    ? "bg-red-900/30 text-red-400 border border-red-500/30"
                    : status === "generating"
                    ? "bg-blue-900/30 text-blue-400 border border-blue-500/30"
                    : status === "reviewing" && isCurrent
                    ? "bg-blue-900/40 text-blue-300 border-2 border-blue-500/50"
                    : status === "reviewing"
                    ? "bg-blue-900/30 text-blue-400 border border-blue-500/30 hover:bg-blue-900/50 cursor-pointer"
                    : "bg-slate-800/50 text-slate-500 border border-slate-700/30"
                } ${!isClickable ? "cursor-default" : ""}`}
              >
                {status === "approved" && (
                  <svg className="w-3.5 h-3.5 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {status === "failed" && (
                  <svg className="w-3.5 h-3.5 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                )}
                {status === "generating" && (
                  <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                )}
                {status === "reviewing" && isCurrent && (
                  <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0" />
                )}
                {STAGE_LABELS[stage] || stage}
              </button>
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
          <div
            data-testid="progress-bar-fill"
            className="h-full bg-green-500 rounded-full transition-all duration-500"
            style={{ width: `${percent}%` }}
          />
        </div>
        <span data-testid="progress-text" className="text-xs text-slate-400 whitespace-nowrap">
          {approvedCount}/{STAGE_ORDER.length} complete
        </span>
      </div>
    </div>
  );
}
